/**
 * Shared order-line stock checks for Excel import, copy-order, grid edits, and save.
 * Aligns with OUT_OF_STOCK_QTY + AR(STCD)=1 + excluded-item rules used on the add form.
 */

export const STOCK_CHECK_EXCLUDED_ITEMS = new Set([
    '27990',
    '27991',
    '27992',
    '27993',
    '27994',
    '27995'
]);

export interface OrderLineStockGuardContext {
    ruleActiveForDivision: boolean;
    isCreditNoteOrder: boolean;
    /** Optional override; defaults to {@link STOCK_CHECK_EXCLUDED_ITEMS}. */
    isStockCheckExcluded?: (itemNumber: unknown) => boolean;
}

export interface StockBlockedLine {
    itemNumber: string;
    lineNumber?: string;
    reason: string;
}

export function isStockCheckExcludedItem(itemNumber: unknown): boolean {
    return STOCK_CHECK_EXCLUDED_ITEMS.has(String(itemNumber ?? '').trim());
}

export function resolveLineAr(line: any): number {
    const raw = line?.ar ?? line?.AR ?? line?.STCD;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
}

export function quantityCommittedFromLine(line: any): number {
    const status = Number(line?.status ?? 0);
    const qty = Number(line?.quantity ?? line?.qty ?? 0);
    if (status === 20) {
        return 0;
    }
    if (status === 10) {
        return qty;
    }
    if (status === 30) {
        const originalQty = line.originalQuantity !== undefined ? Number(line.originalQuantity) : qty;
        const currentQty = qty;
        const originalStatus = line.originalStatus;
        if (originalStatus === 10) {
            return currentQty;
        }
        if (originalStatus === 20) {
            return Math.max(0, currentQty - originalQty);
        }
        if (line.originalQuantityWasSaved === true) {
            return Math.max(0, currentQty - originalQty);
        }
        return currentQty;
    }
    return qty;
}

export function buildStockLedger(existingItemList: any[]): Record<string, number> {
    const ledger: Record<string, number> = {};
    for (const line of existingItemList || []) {
        const itno = String(line?.itemNumber ?? line?.itemNo ?? '').trim();
        if (!itno) {
            continue;
        }
        ledger[itno] = (ledger[itno] || 0) + quantityCommittedFromLine(line);
    }
    return ledger;
}

function clearStockError(row: any): void {
    if (row && 'copyOrderStockError' in row) {
        delete row.copyOrderStockError;
    }
}

/**
 * Updates avlStock / copyOrderStockError for one row using a running per-item ledger.
 * Mutates {@code row} and {@code ledger}.
 */
export function applyRunningStockCheck(
    row: any,
    apiStock: number | null,
    ledger: Record<string, number>,
    ruleActiveForDivision: boolean,
    stockExcluded: boolean
): void {
    if (!row) {
        return;
    }

    const itno = String(row.itemNumber ?? row.itemNo ?? '').trim();
    const qty = Number(row.quantity ?? row.qty ?? 0);
    const ar = resolveLineAr(row);
    const enforce = ruleActiveForDivision && ar === 1 && !stockExcluded;

    if (apiStock === null || !Number.isFinite(apiStock)) {
        row.apiStock = row.apiStock ?? 0;
        row.avlStock = 0;
        if (enforce) {
            row.copyOrderStockError = 'Item or stock could not be verified. Reduce quantity or delete line.';
        } else {
            clearStockError(row);
        }
        return;
    }

    row.apiStock = apiStock;
    const committed = ledger[itno] || 0;
    const remaining = Math.max(0, apiStock - committed);
    row.avlStock = remaining;

    if (enforce) {
        if (remaining < qty) {
            row.copyOrderStockError = `Insufficient stock. Available: ${remaining}. Reduce quantity or delete line.`;
        } else {
            clearStockError(row);
        }
    } else {
        clearStockError(row);
    }

    ledger[itno] = committed + qty;
}

function resolveApiStockForRefresh(row: any): number | null {
    if (row?.apiStock !== undefined && row?.apiStock !== null && Number.isFinite(Number(row.apiStock))) {
        return Number(row.apiStock);
    }
    // Unverified / failed stock fetch — force verification path when enforced
    if (row?.stockError === true) {
        return null;
    }
    // Stale "could not verify" with no usable apiStock
    const msg = String(row?.copyOrderStockError || '').toLowerCase();
    if (msg.includes('could not be verified')) {
        return null;
    }
    // Last resort: treat stored avlStock as remaining before this line only if ledger is empty for item
    // (safe for single-line cases). Multi-line same SKU requires apiStock.
    if (row?.avlStock !== undefined && row?.avlStock !== null && Number.isFinite(Number(row.avlStock))) {
        return Number(row.avlStock);
    }
    if (row?.copyOrderStockError) {
        return null;
    }
    return null;
}

/**
 * Recomputes stock errors for all lines (running ledger). Soft: never throws.
 * @returns lines that must block save
 */
export function refreshOrderLinesStockErrors(
    lines: any[] | null | undefined,
    ctx: OrderLineStockGuardContext
): StockBlockedLine[] {
    const blocked: StockBlockedLine[] = [];
    try {
        if (!lines?.length) {
            return blocked;
        }

        if (ctx.isCreditNoteOrder) {
            for (const row of lines) {
                clearStockError(row);
                if (row) {
                    row.stockError = false;
                }
            }
            return blocked;
        }

        const isExcluded = ctx.isStockCheckExcluded || isStockCheckExcludedItem;
        const ledger: Record<string, number> = {};

        for (const row of lines) {
            if (!row) {
                continue;
            }
            const itemNumber = String(row.itemNumber ?? row.itemNo ?? '').trim();
            if (!itemNumber) {
                continue;
            }

            const stockExcluded = isExcluded(itemNumber);
            const apiStock = resolveApiStockForRefresh(row);

            // When we only have "remaining" avlStock and no apiStock, reconstruct warehouse stock
            // for the first occurrence of the item so ledger math stays consistent.
            let effectiveApi = apiStock;
            if (
                effectiveApi === null &&
                row.avlStock !== undefined &&
                row.avlStock !== null &&
                Number.isFinite(Number(row.avlStock)) &&
                !(row.stockError === true) &&
                !String(row.copyOrderStockError || '')
                    .toLowerCase()
                    .includes('could not be verified')
            ) {
                effectiveApi = Number(row.avlStock) + (ledger[itemNumber] || 0);
            }

            // Prefer explicit apiStock once known
            if (row.apiStock !== undefined && row.apiStock !== null && Number.isFinite(Number(row.apiStock))) {
                effectiveApi = Number(row.apiStock);
            }

            applyRunningStockCheck(row, effectiveApi, ledger, ctx.ruleActiveForDivision, stockExcluded);

            if (row.copyOrderStockError) {
                blocked.push({
                    itemNumber,
                    lineNumber: row.lineNumber != null ? String(row.lineNumber) : undefined,
                    reason: String(row.copyOrderStockError)
                });
            }
        }
    } catch {
        // Fail closed when rule is active: if refresh blows up, block save when any prior error flag exists
        if (ctx.ruleActiveForDivision && !ctx.isCreditNoteOrder) {
            for (const row of lines || []) {
                if (row?.copyOrderStockError || row?.stockError) {
                    blocked.push({
                        itemNumber: String(row.itemNumber ?? row.itemNo ?? ''),
                        lineNumber: row.lineNumber != null ? String(row.lineNumber) : undefined,
                        reason: String(row.copyOrderStockError || 'Stock could not be verified.')
                    });
                }
            }
        }
    }
    return blocked;
}

/** Pure read of whether any line currently has a blocking stock flag (no mutation). */
export function hasBlockingStockFlags(
    lines: any[] | null | undefined,
    ctx: OrderLineStockGuardContext
): boolean {
    if (ctx.isCreditNoteOrder || !ctx.ruleActiveForDivision || !lines?.length) {
        return false;
    }
    const isExcluded = ctx.isStockCheckExcluded || isStockCheckExcludedItem;
    for (const row of lines) {
        if (!row) {
            continue;
        }
        const itemNumber = String(row.itemNumber ?? row.itemNo ?? '').trim();
        if (!itemNumber || isExcluded(itemNumber)) {
            continue;
        }
        if (resolveLineAr(row) !== 1) {
            continue;
        }
        if (row.copyOrderStockError) {
            return true;
        }
        const apiStock = row.apiStock;
        const hasApi = apiStock !== undefined && apiStock !== null && !isNaN(Number(apiStock));
        if (row.stockError && !hasApi) {
            return true;
        }
    }
    return false;
}

export function buildStockSaveBlockMessage(blocked: StockBlockedLine[]): string {
    if (!blocked.length) {
        return 'Some lines have stock errors. Reduce quantity or delete those lines before saving.';
    }
    const preview = blocked
        .slice(0, 3)
        .map((b) => (b.lineNumber ? `Line ${b.lineNumber} (${b.itemNumber})` : b.itemNumber))
        .join(', ');
    const more = blocked.length > 3 ? ` and ${blocked.length - 3} more` : '';
    return `Insufficient or unverified stock on ${preview}${more}. Reduce quantity or delete those lines before saving.`;
}
